import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { addGearWeight, updateGearWeight, removeGearWeight } from '../../services/athleteService';

const today = () => new Date().toISOString().slice(0, 10);

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
}

function GearWeightForm({ initial, onSave, onCancel, saving, error }) {
  const [date, setDate] = useState(initial?.effective_date || today());
  const [weight, setWeight] = useState(initial?.gear_weight_lb ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          type="date" label="Effective Date" size="small" value={date} onChange={e => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} sx={{ minWidth: 160 }}
        />
        <TextField
          type="number" label="Gear Weight (lb)" size="small" value={weight} onChange={e => setWeight(e.target.value)}
          inputProps={{ min: 0, step: 0.1 }} sx={{ minWidth: 150 }}
        />
        <TextField
          label="Note (optional)" size="small" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="e.g. added back plate" sx={{ minWidth: 220, flex: 1 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
        <Button
          variant="contained" color="primary" disabled={saving || weight === ''}
          onClick={() => onSave({ effectiveDate: date, gearWeightLb: Number(weight), notes })}
          sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save'}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8', height: 40 }}>
          Cancel
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

/**
 * Equipment Load — small supporting card, not a major page. Current Gear
 * Weight is simply the latest active record by effective_date (computed
 * here client-side; there's no server-side cache for it, unlike Current
 * Height/Weight, since nothing else derives from it server-side). Every
 * update creates a NEW dated fact via add_athlete_gear_weight, never an
 * overwrite of history — a same-day re-add corrects that day's entry in
 * place instead of creating a second row, same philosophy as
 * measurements. Removing is a soft delete, same as measurements.
 */
export default function GearWeightHistory({ athlete, gearWeights, currentUserId, isAdmin, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const current = gearWeights.length > 0 ? gearWeights[0] : null; // already ordered latest-first

  const handleAdd = async (values) => {
    setSaving(true);
    setFormError(null);
    try {
      await addGearWeight({ athleteId: athlete.id, ...values });
      setAdding(false);
      await onChanged();
    } catch (e) {
      setFormError(e.message || 'Could not save gear weight.');
    } finally {
      setSaving(false);
    }
  };

  const handleCorrect = async (gearWeightId, values) => {
    setSaving(true);
    setFormError(null);
    try {
      await updateGearWeight(gearWeightId, values);
      setEditingId(null);
      await onChanged();
    } catch (e) {
      setFormError(e.message || 'Could not save correction.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    setSaving(true);
    setRemoveError(null);
    try {
      await removeGearWeight(removingId);
      setRemovingId(null);
      await onChanged();
    } catch (e) {
      setRemoveError(e.message || 'Could not remove that gear weight entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mt: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Equipment Load</Typography>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
          Total pads/helmet weight, over time — used only to estimate body weight from a With Gear scale reading.
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em' }}>CURRENT GEAR WEIGHT</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem' }}>{current ? `${current.gear_weight_lb} lb` : '—'}</Typography>
        </Box>

        {!adding ? (
          <Button size="small" variant="outlined" color="primary" onClick={() => { setAdding(true); setFormError(null); }} sx={{ textTransform: 'none', fontWeight: 700, mb: 2 }}>
            + Update Gear Weight
          </Button>
        ) : (
          <Box sx={{ mb: 2.5, p: 2, border: '1px solid #1e2a3a', borderRadius: 2 }}>
            <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 1.5 }}>
              This adds a new dated record — it never overwrites past gear weights. If there's already an entry for this date, your value replaces it.
            </Typography>
            <GearWeightForm onSave={handleAdd} onCancel={() => { setAdding(false); setFormError(null); }} saving={saving} error={formError} />
          </Box>
        )}

        {gearWeights.length === 0 ? (
          <Typography sx={{ color: '#6a8aa8' }}>No gear weight recorded yet.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Gear Weight</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gearWeights.map(g => {
                  const canEdit = g.entered_by === currentUserId || isAdmin;
                  if (editingId === g.id) {
                    return (
                      <TableRow key={g.id}>
                        <TableCell colSpan={4}>
                          <GearWeightForm
                            initial={g}
                            onSave={(values) => handleCorrect(g.id, values)}
                            onCancel={() => { setEditingId(null); setFormError(null); }}
                            saving={saving}
                            error={formError}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <TableRow key={g.id}>
                      <TableCell>{formatDate(g.effective_date)}</TableCell>
                      <TableCell>{g.gear_weight_lb} lb</TableCell>
                      <TableCell sx={{ color: '#6a8aa8' }}>{g.notes || '—'}</TableCell>
                      <TableCell align="right">
                        {canEdit && (
                          <>
                            <IconButton size="small" onClick={() => { setEditingId(g.id); setAdding(false); setFormError(null); }} aria-label="Edit gear weight" sx={{ color: '#6a8aa8' }}>
                              ✎
                            </IconButton>
                            <IconButton size="small" onClick={() => { setRemovingId(g.id); setRemoveError(null); }} aria-label="Remove gear weight" sx={{ color: '#6a8aa8' }}>
                              ✕
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      <Dialog open={removingId !== null} onClose={() => !saving && setRemovingId(null)}>
        <DialogTitle>Remove this gear weight entry from {athlete.first_name}'s history?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>
            It won't be shown in the normal history, and Estimated Body Weight will use the next most recent entry instead.
          </Typography>
          {removeError && <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingId(null)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirmed} disabled={saving} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
